import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Center,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Wrap,
  WrapItem,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { MdDelete, MdEdit } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Card from '@/components/ui/Card/Card';

import { useDeleteSurveyMutation } from '../api';
import { Survey } from '../types';
import { formatSurveyDate, getSurveyVisibilityLabel } from '../utils';

type SurveyListProps = {
  surveys?: Survey[];
  isLoading?: boolean;
};

const getAuthorLabel = (survey: Survey) => {
  if (!survey.author) {
    return '—';
  }

  const { firstName, lastName, email } = survey.author;
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  if (fullName && email) {
    return `${fullName} (${email})`;
  }

  return fullName || email || '—';
};

export const SurveyList = ({ surveys, isLoading }: SurveyListProps) => {
  const navigate = useNavigate();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const [surveyToDelete, setSurveyToDelete] = useState<Survey | null>(null);
  const [deleteSurvey, { isLoading: isDeleting }] = useDeleteSurveyMutation();

  const handlePreview = (survey: Survey) => {
    navigate(`/app/surveys/${survey.globalId}`);
  };

  const handleEdit = (survey: Survey) => {
    navigate(`/app/surveys/${survey.globalId}/edit`);
  };

  const handleDeleteClick = (survey: Survey) => {
    setSurveyToDelete(survey);
    onDeleteOpen();
  };

  const handleCloseDialog = () => {
    setSurveyToDelete(null);
    onDeleteClose();
  };

  const handleConfirmDelete = async () => {
    if (!surveyToDelete) {
      return;
    }
    try {
      await deleteSurvey(surveyToDelete.globalId).unwrap();
      toast.success('Ankieta została usunięta.');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Nie udało się usunąć ankiety.');
    } finally {
      handleCloseDialog();
    }
  };

  if (isLoading) {
    return (
      <Card>
        <Center py={10}>
          <Spinner color="purple.500" />
        </Center>
      </Card>
    );
  }

  if (!surveys || surveys.length === 0) {
    return (
      <Card>
        <Stack spacing={2} textAlign="center" py={6} color="gray.800">
          <Text fontWeight="semibold">Brak ankiet</Text>
          <Text fontSize="sm" color="gray.600">
            Użyj przycisku „Nowa ankieta”, aby przygotować pierwsze badanie.
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Box overflowX="auto" color="gray.800">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th color="gray.600">Nazwa</Th>
                <Th color="gray.600">Widoczność</Th>
                <Th color="gray.600">Okres</Th>
                <Th color="gray.600">Autor</Th>
                <Th color="gray.600">Status</Th>
                <Th color="gray.600">Domeny</Th>
                <Th color="gray.600" textAlign="right">
                  Akcje
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {surveys.map((survey) => (
                <Tr
                  key={survey.globalId}
                  cursor="pointer"
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => handlePreview(survey)}
                >
                  <Td>
                    <Stack spacing={1}>
                      <Text fontWeight="medium">{survey.title}</Text>
                      <Text fontSize="xs" color="gray.500">
                        Utworzono: {dayjs(survey.createdAt).format('DD.MM.YYYY HH:mm')}
                      </Text>
                    </Stack>
                  </Td>
                  <Td>{getSurveyVisibilityLabel(survey.visibilityScope)}</Td>
                  <Td>
                    <Stack spacing={0} fontSize="sm">
                      <Text>Start: {formatSurveyDate(survey.startDate)}</Text>
                      <Text>Koniec: {formatSurveyDate(survey.endDate)}</Text>
                    </Stack>
                  </Td>
                  <Td>{getAuthorLabel(survey)}</Td>
                  <Td>
                    <Badge colorScheme={survey.isPublished ? 'green' : 'gray'}>
                      {survey.isPublished ? 'Opublikowana' : 'Szkic'}
                    </Badge>
                  </Td>
                  <Td>
                    {survey.schemas && survey.schemas.length > 0 ? (
                      <Wrap spacing={1}>
                        {survey.schemas.map((schema) => (
                          <WrapItem key={schema}>
                            <Badge colorScheme="purple" variant="subtle">
                              {schema}
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        Wszystkie
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <HStack justify="flex-end" spacing={1}>
                      {!survey.isPublished ? (
                        <IconButton
                          aria-label="Edytuj ankietę"
                          icon={<MdEdit />}
                          size="sm"
                          variant="ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEdit(survey);
                          }}
                        />
                      ) : null}
                      <IconButton
                        aria-label="Usuń ankietę"
                        icon={<MdDelete />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteClick(survey);
                        }}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Card>

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleCloseDialog}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="semibold">
              Usuń ankietę
            </AlertDialogHeader>

            <AlertDialogBody>
              {surveyToDelete ? (
                <Text>
                  Czy na pewno chcesz usunąć ankietę{' '}
                  <Text as="span" fontWeight="semibold">
                    {surveyToDelete.title}
                  </Text>
                  ? Tego działania nie można cofnąć.
                </Text>
              ) : (
                <Text>Czy na pewno chcesz usunąć tę ankietę? Tego działania nie można cofnąć.</Text>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCloseDialog}>
                Anuluj
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3} isLoading={isDeleting}>
                Usuń
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
